/**
 * The sidebar's structure: grouped sections, each a subset of the same
 * taxonomy `module-tiles.ts` uses for the dashboard grid — the two are
 * meant to tell the same story about how the product is organized, not
 * invent a second one. A hidden item is a usability choice, never a
 * security control; the API rejects an unpermitted request regardless of
 * what the client renders.
 */
export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
  /** Null means always visible. */
  readonly permission: string | null;
  /** True when another nav item's `to` starts with this one's — otherwise
   *  the sidebar would highlight both this item and that more specific one
   *  at once (e.g. "Chart of Accounts" staying lit while on "Reports"). */
  readonly end?: boolean;
}

export interface NavSection {
  readonly id: string;
  /** Null renders no header — reserved for the single always-visible
   *  Dashboard entry at the top. */
  readonly labelKey: string | null;
  readonly items: readonly NavItem[];
  /** Marks the TABITA-operator section, which only PLATFORM_SUPER_ADMIN
   *  ever sees, for the distinct treatment `layout.css` gives it — the
   *  same "this does something different" flag `.settings-card-test`
   *  already uses for a real outbound send. */
  readonly variant?: 'platform';
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'home',
    labelKey: null,
    items: [{ to: '/', labelKey: 'navigation.dashboard', permission: null }],
  },
  {
    id: 'community',
    labelKey: 'navigation.community',
    items: [
      { to: '/people', labelKey: 'navigation.people', permission: 'people.person.read' },
      { to: '/groups', labelKey: 'groups.group.title', permission: 'groups.group.read' },
      { to: '/events', labelKey: 'navigation.events', permission: 'events.event.read' },
      {
        to: '/attendance',
        labelKey: 'navigation.attendance',
        permission: 'attendance.record.read',
      },
      { to: '/calendar', labelKey: 'navigation.calendar', permission: 'calendar.event.read' },
      { to: '/kids', labelKey: 'kids.checkin.title', permission: 'kids.checkin.read' },
      { to: '/forms', labelKey: 'navigation.forms', permission: 'forms.form.read' },
    ],
  },
  {
    id: 'communications',
    labelKey: 'navigation.communications',
    items: [
      {
        to: '/communications',
        labelKey: 'communications.campaign.title',
        permission: 'communications.campaign.read',
      },
      {
        to: '/notifications',
        labelKey: 'notifications.log.title',
        permission: 'notification.log.read',
      },
    ],
  },
  {
    id: 'finance',
    labelKey: 'navigation.givingAndFinance',
    items: [
      { to: '/giving', labelKey: 'giving.donation.title', permission: 'giving.donation.read' },
      {
        to: '/finance',
        labelKey: 'finance.account.title',
        permission: 'finance.account.read',
        end: true,
      },
      {
        to: '/finance/journal-entries',
        labelKey: 'finance.journalEntry.title',
        permission: 'finance.journal_entry.read',
      },
      {
        to: '/finance/reports',
        labelKey: 'finance.reports.title',
        permission: 'finance.account.read',
      },
      {
        to: '/billing',
        labelKey: 'billing.subscription.title',
        permission: 'billing.subscription.read',
      },
    ],
  },
  {
    id: 'academic',
    labelKey: 'navigation.academic',
    items: [
      {
        to: '/academic/schools',
        labelKey: 'academic.school.title',
        permission: 'academic.school.read',
      },
      {
        to: '/academic/my',
        labelKey: 'academic.my.title',
        permission: 'academic.enrollment.self',
      },
      {
        to: '/academic/homologations',
        labelKey: 'academic.homologation.title',
        permission: 'academic.homologation.create',
      },
    ],
  },
  {
    id: 'inventory',
    labelKey: 'navigation.inventory',
    items: [
      {
        to: '/inventory',
        labelKey: 'inventory.item.navLabel',
        permission: 'inventory.item.read',
        end: true,
      },
      {
        to: '/inventory/checkouts',
        labelKey: 'inventory.checkout.title',
        permission: 'inventory.checkout.read',
      },
      {
        to: '/inventory/categories',
        labelKey: 'inventory.category.title',
        permission: 'inventory.category.read',
      },
      {
        to: '/inventory/locations',
        labelKey: 'inventory.location.title',
        permission: 'inventory.location.read',
      },
      {
        to: '/inventory/suppliers',
        labelKey: 'inventory.supplier.title',
        permission: 'inventory.supplier.read',
      },
    ],
  },
  {
    id: 'system',
    labelKey: 'navigation.system',
    items: [
      { to: '/cloud', labelKey: 'navigation.myCloud', permission: 'file.object.read' },
      {
        to: '/administration',
        labelKey: 'navigation.administration',
        permission: 'tenancy.tenant.admin',
        end: true,
      },
      {
        to: '/administration/custom-fields',
        labelKey: 'people.customFields.manageLink',
        permission: 'people.customField.read',
      },
      {
        to: '/administration/roles',
        labelKey: 'administration.roles.title',
        permission: 'authorization.role.read',
      },
      {
        to: '/administration/appearance',
        labelKey: 'administration.appearance.title',
        permission: 'tenancy.tenant.admin',
      },
    ],
  },
  {
    id: 'platform',
    labelKey: 'navigation.platform',
    variant: 'platform',
    items: [
      {
        to: '/platform/subscriptions',
        labelKey: 'platform.subscriptions.navLabel',
        permission: 'platform.tenant.read',
      },
      {
        to: '/platform/modules',
        labelKey: 'platform.modules.navLabel',
        permission: 'platform.module.read',
      },
      {
        to: '/platform/smtp',
        labelKey: 'platform.smtp.navLabel',
        permission: 'platform.settings.read',
      },
      {
        to: '/platform/storage',
        labelKey: 'platform.storage.navLabel',
        permission: 'platform.settings.read',
      },
      {
        to: '/platform/oauth-providers',
        labelKey: 'platform.oauth.navLabel',
        permission: 'platform.settings.read',
      },
    ],
  },
  {
    id: 'maintenance',
    labelKey: 'navigation.maintenance',
    variant: 'platform',
    items: [
      {
        to: '/platform/denominations',
        labelKey: 'platform.denominations.navLabel',
        permission: 'platform.denomination.read',
      },
      {
        to: '/platform/councils',
        labelKey: 'platform.councils.navLabel',
        permission: 'platform.council.read',
      },
    ],
  },
];
